import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { User, UserType } from 'api/models';
import { useNativeMembers } from 'data/members/hooks';
import { useUser } from 'hooks/use-user';
import { useUsersQuery } from 'queries/members';
import { isValidEmail as checkEmailRegex } from 'utils/is-valid-email';

const ErrorMessage = styled.div`
  color: var(--red-60);
`;

export const useInviteUserHelpers = ({
  allInProjectUsers,
  partnerUser,
}: {
  allInProjectUsers: User[];
  partnerUser: boolean;
}): {
  isProjectMember: (emailId: string) => boolean;
  isNativeMember: (emailId: string) => boolean;
  isEmailValid: (emailId: string) => boolean;
  getNoOptionsMessage: (params) => JSX.Element;
} => {
  const {
    user: { company: currentUserCompany, userType: currentUserType },
  } = useUser();

  const { data: users } = useUsersQuery();
  const { members: nativeMembers } = useNativeMembers();
  const isNativeMember = useCallback(
    (emailId: string) =>
      nativeMembers.some((members) => members.user.emailId === emailId),
    [nativeMembers],
  );

  const isProjectMember = useCallback(
    (emailId: string) =>
      allInProjectUsers.some((members) => members.emailId === emailId),
    [allInProjectUsers],
  );

  const isDuplicateInput = useCallback(
    (emailId: string) => users?.find((user) => user.emailId === emailId),
    [users],
  );

  const isEmailValid = useCallback(
    (emailId: string) =>
      checkEmailRegex(emailId) &&
      !isDuplicateInput(emailId) &&
      !isProjectMember(emailId),
    [isDuplicateInput, isProjectMember],
  );

  const getNoOptionsMessage = ({ inputValue }) => {
    if (partnerUser) {
      return <FormattedMessage id="no.options" />;
    }
    if (isProjectMember(inputValue)) {
      return (
        <ErrorMessage>
          <FormattedMessage
            id="existing.invitation"
            values={{
              user: inputValue,
            }}
          />
        </ErrorMessage>
      );
    }

    const existingUser = users?.find((user) => user.emailId === inputValue);

    if (existingUser != null) {
      if (existingUser.company.companyId === currentUserCompany.companyId) {
        return (
          <ErrorMessage>
            <FormattedMessage
              id="request.invitation.errors.part_of_current_user_team"
              values={{
                user: inputValue,
              }}
            />
          </ErrorMessage>
        );
      }

      return currentUserType === UserType.Native ? (
        <ErrorMessage>
          <FormattedMessage
            id="request.invitation.errors.part_of_another_team"
            values={{
              companyName: existingUser.company.companyName,
              type: existingUser.company.companyType,
            }}
          />
        </ErrorMessage>
      ) : (
        <ErrorMessage>
          <FormattedMessage id="request.invitation.errors.part_of_another_team.for_non_vendor" />
        </ErrorMessage>
      );
    }

    return <FormattedMessage id="validations.invite_email" />;
  };

  return {
    isProjectMember,
    isNativeMember,
    isEmailValid,
    getNoOptionsMessage,
  };
};
